import { Menu, MenuButton, MenuItems, MenuItem, Transition } from "@headlessui/react";
import { ApiError } from "@wexinc/wex-common-web";
import {
  Button,
  ChevronDownIcon,
  Heading,
  Label,
  LoadableContentContainer,
  PenIcon,
  PlusIcon,
  SearchInput,
  TrashIcon,
  UserGroupIcon,
  UserPlusIcon,
} from "@wexinc/wex-ui";
import { CopAdminRoutes, CopRoutes } from "app/routes";
import organizationLandingLogo from "assets/images/organization-landing-logo.svg";
import { produce } from "immer";
import { debounce } from "lodash";
import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AddMemberResponse,
  OrganizationDetails,
  OrganizationInfo,
  OrganizationInfoExpanded,
  OrganizationInvitation,
  OrganizationLineage,
  OrganizationMember,
  ParentOrganizationInfo,
  isOrganizationDetails,
  settings,
} from "services/organization";
import useCOPOrganizationService from "services/organization/useCOPOrganizationService";
import { CopPermissionIds, useCopSecurityService } from "services/security";
import AddOrUpdateOrganizationModal from "./components/AddOrUpdateOrganizationModal";
import AddOrganizationMembersModal from "./components/AddOrganizationMembersModal";
import DeleteOrganizationConfirmModal from "./components/DeleteOrganizationConfirmModal";
import EditOrganizationFlyout from "./components/EditOrganizationFlyout";
import OrganizationBreadcrumb from "./components/OrganizationBreadcrumb";
import { InvitationActions } from "./components/OrganizationInvitesGrid";
import OrganizationsGrid from "./components/OrganizationsGrid";

const editOrgQueryParam = "edit";

const Organizations: React.FC = () => {
  const [search, setSearch] = useState<string>();
  const onSearch = debounce((value: string) => setSearch(value), 200);
  const copOrganizationService = useCOPOrganizationService();
  const [organizations, setOrganizations] = useState<OrganizationInfoExpanded[]>([]);
  const [showAddEditOrganizationModal, setShowAddEditOrganizationModal] = useState<{
    show: boolean;
    existingOrganization?: OrganizationInfo;
    parentOrganization?: ParentOrganizationInfo;
  }>({ show: false });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [organizationInEdit, setOrganizationInEdit] = useState<OrganizationDetails>();
  const [editOrgFlyoutIsVisible, setEditOrgFlyoutIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [organizationToDelete, setOrganizationToDelete] = useState<OrganizationInfo>();
  const [showAddMembersModal, setShowAddMembersModal] = useState<{
    show: boolean;
    canEditRoles: boolean;
    canAssignOrgOwnerRole: boolean;
    organization?: OrganizationInfo;
  }>({ show: false, canEditRoles: false, canAssignOrgOwnerRole: false });
  const [canAddNestedOrganizations, setCanAddNestedOrganizations] = useState<boolean>();
  const [currentOrganization, setCurrentOrganization] = useState<OrganizationLineage>();
  const copSecurityService = useCopSecurityService();
  const disallowNewOrganizationReason = `Maximum of ${settings.maxOrganizationNestingLevels} levels allowed.`;

  const [currentOrgPerms, setCurrentOrgPerms] = useState<CopPermissionIds[]>([]);
  const [orgInEditPerms, setOrgInEditPerms] = useState<CopPermissionIds[]>([]);

  async function loadPage() {
    const currentOrgId = copOrganizationService.getCurrentOrganizationId();
    const currentOrg = currentOrgId ? await getOrgLineage(currentOrgId!) : undefined;

    let organizationsToShow: OrganizationInfoExpanded[];

    if (currentOrg) {
      organizationsToShow = currentOrg.children;
      setCanAddNestedOrganizations(copOrganizationService.canAddNestedOrganizations(currentOrg));
      setCurrentOrgPerms(
        await copSecurityService.filterUserOrgPerms(currentOrg.id, [
          "createOrganization",
          "editMembers",
          "editOrganization",
          "deleteOrganization",
          "editRoles",
          "editOrgOwner",
        ]),
      );
    } else {
      setCanAddNestedOrganizations(true);
      organizationsToShow = await copOrganizationService.getTopLevelOrganizations();
      setCurrentOrgPerms([]);
    }

    setOrganizations(organizationsToShow);
    setIsLoading(false);
    setCurrentOrganization(currentOrg);
  }

  const relevantErrorCodes = ["E-COP-2013", "E-CMN-1041", "E-CMN-1041", "E-COP-2025", "E-CMN-1009", "E-CMN-1010"];

  async function getOrgLineage(orgId: string) {
    try {
      const currentOrganization = await copOrganizationService.getOrganizationLineage(orgId);
      return currentOrganization;
    } catch (e) {
      if (e instanceof ApiError && e.errorCodes?.some((err) => relevantErrorCodes.includes(err))) {
        navigate(`/org-not-found`);
      }
    }
  }

  function goToOrganization(organizationId?: string) {
    navigate(organizationId || `/${CopRoutes.admin}/${CopAdminRoutes.organizations}`);
  }

  async function showEditOrgFlyout(organizationId: string) {
    setEditOrgFlyoutIsVisible(true);
    setOrganizationInEdit(await copOrganizationService.getOrganizationDetails(organizationId));
    setOrgInEditPerms(await copSecurityService.filterUserOrgPerms(organizationId, ["editRoles", "editOrgOwner"]));
  }

  function closeEditOrgFlyout() {
    setEditOrgFlyoutIsVisible(false);
    if (searchParams.get(editOrgQueryParam)) {
      setSearchParams((p) => {
        p.delete(editOrgQueryParam);
        return p;
      });
    }
    setOrganizationInEdit(undefined);
    setOrgInEditPerms([]);
  }

  function editCurrentOrg() {
    setSearchParams((p) => {
      p.set(editOrgQueryParam, currentOrganization!.id);
      return p;
    });
  }

  function showAddOrUpdateOrgDialog(
    existingOrganization?: OrganizationInfo,
    parentOrganization?: ParentOrganizationInfo,
  ) {
    setShowAddEditOrganizationModal({
      show: true,
      existingOrganization,
      parentOrganization: parentOrganization,
    });
  }

  function showAddAddOrMemberModal(organization: OrganizationInfo) {
    setShowAddMembersModal({
      show: true,
      organization,
      canEditRoles: hasPermission(organization, "editRoles"),
      canAssignOrgOwnerRole: hasPermission(organization, "editOrgOwner"),
    });
  }

  function closeAddOrgMemberModal() {
    setShowAddMembersModal({
      show: false,
      canEditRoles: false,
      canAssignOrgOwnerRole: false,
    });
  }

  async function onOrgAddedOrUpdated(isNew: boolean, organization: OrganizationInfo | OrganizationDetails) {
    closeAddOrUpdateOrgDialog();
    if (isNew) {
      goToOrganization(organization.id);
    } else {
      if (currentOrganization && currentOrganization.id === organization.id) {
        setCurrentOrganization(
          produce((draft) => {
            draft!.name = organization.name;
            draft!.description = organization.description;
            draft!.accessType = organization.accessType;
            draft!.contactEmail = organization.contactEmail;
          }),
        );
      } else {
        const updatedOrganizations = produce(organizations, (draft) => {
          const index = draft.findIndex((i) => i.id === organization.id);
          draft[index].name = organization.name;
          draft[index].description = organization.description;
        });
        setOrganizations(updatedOrganizations);
      }
      if (organization.id === organizationInEdit?.id) {
        setOrganizationInEdit(
          produce((draft) => {
            draft!.name = organization.name;
            draft!.description = organization.description;
            draft!.accessType = organization.accessType;
            draft!.contactEmail = organization.contactEmail;
          }),
        );
      }
      closeAddOrUpdateOrgDialog();
    }
  }

  function closeAddOrUpdateOrgDialog() {
    setShowAddEditOrganizationModal({ show: false });
  }

  async function deleteOrganization() {
    setOrganizationToDelete(undefined);

    await copOrganizationService.deleteOrganization(organizationToDelete!.id);

    if (currentOrganization?.id === organizationToDelete?.id) {
      const nextOrgIdToGoTo = organizationToDelete?.parent?.id;
      goToOrganization(nextOrgIdToGoTo);
      return;
    }

    if (organizationInEdit?.id === organizationToDelete?.id) {
      closeEditOrgFlyout();
    }

    setOrganizations(
      produce((draft) => {
        const index = draft.findIndex((i) => i.id === organizationToDelete!.id);
        draft.splice(index, 1);
      }),
    );

    setIsLoading(true);
    loadPage();
  }

  function onOrganizationMembersChanged(
    operation: "add-members" | "update-member" | "delete-member",
    organizationId: string,
    payload: OrganizationMember | AddMemberResponse<OrganizationInvitation>[],
  ) {
    switch (operation) {
      case "add-members": {
        const addMemberResponses = payload as AddMemberResponse<OrganizationInvitation>[];
        const newMembers = addMemberResponses.filter((i) => i.status === "ADDED" && !!i.member).map((i) => i.member!);

        if (organizationId === organizationInEdit?.id) {
          const newInvites = addMemberResponses
            .filter((i) => i.status === "INVITATION_SENT" && !!i.invitation)
            .map((i) => i.invitation!);
          // Update the org in the flyout.
          setOrganizationInEdit(
            produce((draft) => {
              draft!.members.push(...newMembers);
              draft!.invitees.push(...newInvites);
            }),
          );
        }
        if (organizationId !== currentOrganization?.id) {
          // Update the org in the Org grid.
          const updatedOrganizations = produce(organizations, (draft) => {
            const index = draft.findIndex((i) => i.id === organizationId);
            if (index !== -1) {
              draft[index].memberCount += newMembers.length;
            }
          });
          setOrganizations(updatedOrganizations);
        }
        break;
      }
      case "delete-member":
      case "update-member": {
        const affectedMember = payload as OrganizationMember;
        const updateOrganization = (organization: OrganizationDetails | OrganizationInfoExpanded) => {
          return produce(organization, (draft) => {
            if (isOrganizationDetails(draft)) {
              const index = draft.members.findIndex((i) => i.user.id === affectedMember.user.id);
              if (operation === "delete-member") {
                draft.members.splice(index, 1);
              } else {
                draft.members[index] = affectedMember;
              }
            } else {
              if (operation === "delete-member") {
                draft.memberCount -= 1;
              }
            }
          });
        };
        if (organizationId === organizationInEdit?.id) {
          setOrganizationInEdit(updateOrganization(organizationInEdit) as OrganizationDetails);
        }

        if (organizationId !== currentOrganization?.id) {
          setOrganizations(
            produce((draft) => {
              const index = draft.findIndex((i) => i.id === organizationId);
              if (index !== -1) {
                const updatedOrganization = updateOrganization(draft[index]) as OrganizationInfoExpanded;

                draft[index] = updatedOrganization;
              }
            }),
          );
        }

        if (
          operation === "delete-member" &&
          affectedMember.user.id === copSecurityService.securityStore.loggedInUser!.id
        ) {
          if (currentOrganization?.id === organizationId) {
            const nextOrgIdToGoTo = currentOrganization?.parent?.id;
            goToOrganization(nextOrgIdToGoTo);
          } else {
            closeEditOrgFlyout();
          }
          copSecurityService.updatePermissions();
        }
        break;
      }

      default:
        throw Error(`Unsupported operation: ${operation}.`);
    }

    closeAddOrgMemberModal();
  }

  const handleOrgGridOnAction = (organization: OrganizationInfo) => {
    setSearchParams((p) => {
      p.set(editOrgQueryParam, organization.id);
      return p;
    });
  };

  function onInvitationAction(action: InvitationActions, invitation: OrganizationInvitation) {
    switch (action) {
      case "revoke":
        setOrganizationInEdit(
          produce((draft) => {
            const index = draft!.invitees.findIndex((i) => i.id === invitation.id);
            draft!.invitees.splice(index, 1);
          }),
        );

        break;
      default:
        throw Error(`No supported action: '${action}'.`);
    }
  }

  useEffect(() => {
    setIsLoading(true);
    loadPage();
    // eslint-disable-next-line
  }, [copSecurityService.securityStore.loggedInUser]);

  useEffect(() => {
    if (searchParams.get(editOrgQueryParam)) {
      showEditOrgFlyout(searchParams.get(editOrgQueryParam)!);
    } else {
      closeEditOrgFlyout();
    }
    // eslint-disable-next-line
  }, [searchParams]);

  function hasPermission(organization: OrganizationInfo, permission: CopPermissionIds) {
    if (organization.id === currentOrganization?.id) {
      return currentOrgPerms.includes(permission);
    }
    if (organization.id == organizationInEdit?.id) {
      return orgInEditPerms.includes(permission);
    }
    throw new Error("unexpected org permission request " + organization.id);
  }

  const userCanManageCurrentOrg =
    currentOrganization &&
    (hasPermission(currentOrganization, "createOrganization") ||
      hasPermission(currentOrganization, "editOrganization") ||
      hasPermission(currentOrganization, "deleteOrganization") ||
      hasPermission(currentOrganization, "editMembers"));

  return (
    <LoadableContentContainer
      showLoading={isLoading}
      className="!top-20"
    >
      <div className="flex items-center justify-between">
        <div className="ml-8 py-3">
          {currentOrganization && <OrganizationBreadcrumb parentInfo={currentOrganization?.parent} />}
          <Heading
            testId="edit-company-name"
            size="large"
          >
            {!currentOrganization && "Organizations"}
            {currentOrganization && currentOrganization.name}
          </Heading>
        </div>
        {!currentOrganization && copSecurityService.userHasGlobalPermission("createOrganization") && (
          <Button
            testId="create-new-organization"
            buttonType="primary"
            className="my-4 px-4 mr-2"
            onClick={() => showAddOrUpdateOrgDialog()}
          >
            <PlusIcon className="mr-2 " />
            New organization
          </Button>
        )}
        {currentOrganization && (
          <Menu
            as="div"
            className="relative inline-block text-left"
          >
            <div className={userCanManageCurrentOrg ? "" : "pl-[47px]"}>
              <MenuButton
                as="div"
                className="px-4 py-2"
              >
                <Button
                  testId="manage-organization"
                  buttonType="primary"
                >
                  {userCanManageCurrentOrg ? "Manage organization" : "Organization"}
                  <ChevronDownIcon
                    className="w-5 h-4 ml-3 -mr-2"
                    aria-hidden="true"
                  />
                </Button>
              </MenuButton>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="absolute shadow-elevation-2 origin-top-right bg-utility-white rounded-lg focus:outline-none pl-3 z-10">
                {hasPermission(currentOrganization!, "createOrganization") && (
                  <MenuItem as="div">
                    <>
                      <Button
                        testId="menu-new-organization"
                        buttonType="secondary-neutral-dark"
                        className="my-4 px-4 mr-2"
                        disabled={!currentOrganization || canAddNestedOrganizations === false}
                        onClick={() => showAddOrUpdateOrgDialog(undefined, currentOrganization)}
                      >
                        <PlusIcon className="mr-2 " />
                        New organization
                      </Button>
                      {canAddNestedOrganizations === false && (
                        <Label
                          data-test-id="max-org-levels-message"
                          className="-mt-2 block"
                        >
                          {disallowNewOrganizationReason}
                        </Label>
                      )}
                    </>
                  </MenuItem>
                )}

                <MenuItem as="div">
                  <Button
                    testId="menu-view-members"
                    buttonType="secondary-neutral-dark"
                    className="my-4 px-4 mr-2"
                    onClick={editCurrentOrg}
                    disabled={!currentOrganization}
                  >
                    <UserGroupIcon className="mr-2 " />
                    View members
                  </Button>
                </MenuItem>

                {hasPermission(currentOrganization!, "editMembers") && (
                  <MenuItem as="div">
                    <Button
                      testId="menu-add-members"
                      buttonType="secondary-neutral-dark"
                      className="my-4 px-4 mr-2"
                      disabled={!currentOrganization}
                      onClick={() => showAddAddOrMemberModal(currentOrganization)}
                    >
                      <UserPlusIcon className="mr-2 " />
                      Add members
                    </Button>
                  </MenuItem>
                )}
                {hasPermission(currentOrganization!, "editOrganization") && (
                  <MenuItem as="div">
                    <Button
                      testId="menu-edit-organization"
                      buttonType="secondary-neutral-dark"
                      className="my-4 px-4 mr-2"
                      disabled={!currentOrganization}
                      onClick={() => showAddOrUpdateOrgDialog(currentOrganization, currentOrganization)}
                    >
                      <PenIcon className="mr-2 " />
                      Edit organization
                    </Button>
                  </MenuItem>
                )}
                {hasPermission(currentOrganization!, "deleteOrganization") && (
                  <MenuItem as="div">
                    <Button
                      testId="menu-delete-organization"
                      buttonType="secondary-critical"
                      className="my-4 px-4 mr-2"
                      disabled={!currentOrganization || currentOrganization.children.length > 0}
                      onClick={() => setOrganizationToDelete(currentOrganization)}
                    >
                      <TrashIcon className="mr-2 " />
                      Delete organization
                    </Button>
                  </MenuItem>
                )}
              </MenuItems>
            </Transition>
          </Menu>
        )}
      </div>
      {editOrgFlyoutIsVisible && (
        <EditOrganizationFlyout
          organizationInEdit={organizationInEdit}
          onEditOrganization={() => showAddOrUpdateOrgDialog(organizationInEdit!, organizationInEdit!.parent)}
          onClose={closeEditOrgFlyout}
          onDeleteOrganization={setOrganizationToDelete}
          onNewOrganization={() => showAddOrUpdateOrgDialog(undefined, organizationInEdit)}
          onAddMembers={() => showAddAddOrMemberModal(organizationInEdit!)}
          onMemberDeleted={(i) => onOrganizationMembersChanged("delete-member", organizationInEdit!.id, i)}
          onMemberUpdated={(i) => onOrganizationMembersChanged("update-member", organizationInEdit!.id, i)}
          disallowNewOrganizationReason={canAddNestedOrganizations ? undefined : disallowNewOrganizationReason}
          onInvitationAction={onInvitationAction}
        />
      )}
      {organizations && organizations.length > 0 && (
        <div className="flex items-center border-b border-t border-t-neutral-dark-20 border-b-neutral-dark-20">
          <SearchInput
            containerClassName="border-none h-9 w-48"
            onChange={(e) => onSearch(e.currentTarget.value)}
            name="search-organizations"
          />
        </div>
      )}
      <div className="px-6">
        {!organizations?.length && !currentOrganization && (
          <Label
            size="medium"
            className="inline-block w-full text-center mt-16"
          >
            (No Organizations)
          </Label>
        )}
        {!organizations?.length && currentOrganization && (
          <div className="flex justify-center mt-32">
            <img
              src={organizationLandingLogo}
              alt="Organization Logo"
            />
          </div>
        )}
        {organizations && organizations.length > 0 && (
          <OrganizationsGrid
            organizations={organizations!}
            globalFilter={search}
            onAction={handleOrgGridOnAction}
            selectedOrganizationId={organizationInEdit?.id}
          />
        )}
      </div>
      {showAddEditOrganizationModal.show && (
        <AddOrUpdateOrganizationModal
          onAddedOrUpdated={onOrgAddedOrUpdated}
          onCancel={closeAddOrUpdateOrgDialog}
          parentOrganization={showAddEditOrganizationModal.parentOrganization}
          existingOrganization={showAddEditOrganizationModal.existingOrganization}
        />
      )}
      {organizationToDelete && (
        <DeleteOrganizationConfirmModal
          onOK={deleteOrganization}
          onCancel={() => setOrganizationToDelete(undefined)}
        />
      )}
      {showAddMembersModal.show && (
        <AddOrganizationMembersModal
          organizationId={showAddMembersModal.organization!.id}
          canEditRoles={showAddMembersModal.canEditRoles}
          canAssignOrgOwnerRole={showAddMembersModal.canAssignOrgOwnerRole}
          onCancel={() => closeAddOrgMemberModal()}
          onMembersAdded={(id, addMemberResponses) =>
            onOrganizationMembersChanged("add-members", id, addMemberResponses)
          }
        />
      )}
    </LoadableContentContainer>
  );
};

export default Organizations;
